import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getProcessCategories,
  createProcessCategory,
  updateProcessCategory,
  deleteProcessCategory,
} from "../services/processCategoryService";
import { useAppContext } from "../context/AppContext";
import "../styles/management-tables.css";
import "../styles/modal.css";
import "../styles/form.css";
import { resolveRoleName } from "../utils/auth";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const ProcessCategoryAttributesPage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProcessCategories();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load process categories:", err);
      setError(err.message || "Failed to load Process Categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const name = (item.name || "").toLowerCase();
      const desc = (item.description || "").toLowerCase();
      const q = searchTerm.toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [items, searchTerm]);

  const openCreateModal = () => {
    setActiveDropdown(null);
    setFormData({ name: "", description: "" });
    setFormError(null);
    setModalMode("create");
    setSelected(null);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setActiveDropdown(null);
    setFormData({
      name: item.name || "",
      description: item.description || "",
    });
    setFormError(null);
    setModalMode("edit");
    setSelected(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
    setFormError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError("Name is required");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        name: formData.name.trim(),
        description: (formData.description || formData.name || "").trim(),
      };
      if (modalMode === "create") {
        await createProcessCategory(payload);
      } else if (modalMode === "edit" && selected) {
        await updateProcessCategory(selected.id, payload);
      }
      await loadData();
      closeModal();
    } catch (err) {
      console.error("Failed to save process category:", err);
      setFormError(err.message || "Failed to save Process Category");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!item) return;
    const label = item.name || item.description || `ID ${item.id}`;
    const shouldDelete = window.confirm(
      `Are you sure you want to delete "${label}"? This action cannot be undone.`,
    );
    if (!shouldDelete) return;

    setDeletingId(item.id);
    try {
      await deleteProcessCategory(item.id);
      toast.success(`Process category "${label}" deleted successfully.`);
      await loadData();
    } catch (err) {
      console.error("Failed to delete process category:", err);
      toast.error(err.message || "Failed to delete Process Category");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading Process Categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-state">
          <p>Error: {error}</p>
          <button className="retry-btn" onClick={loadData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Process Category Attributes</h2>
            <p className="page-subtitle">
              Manage categories for processes
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="secondary-btn sm" onClick={() => setSearchTerm("")}>
                Clear
              </button>
            </div>
            <button className="primary-btn" onClick={openCreateModal}>
              + Create Category
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="kpi-table-container">
          {filtered.length === 0 ? (
            <div className="no-results">
              <p>No process categories found.</p>
              {items.length === 0 && (
                <button className="create-btn" onClick={openCreateModal}>
                  Create your first category
                </button>
              )}
            </div>
          ) : (
            <table className="kpi-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>
                      {item.name || item.description || (
                        <span className="empty-text">-</span>
                      )}
                    </td>
                    <td className="description-cell">
                      {item.description || <span className="empty-text">-</span>}
                    </td>
                    <td className="actions-cell">
                      <div
                        className="actions-menu-wrapper"
                        style={{ position: "relative", display: "inline-block" }}
                      >
                        <button
                          className="action-menu-btn"
                          aria-haspopup="true"
                          aria-expanded={activeDropdown === item.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeDropdown === item.id) {
                              setActiveDropdown(null);
                            } else {
                              setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                              setActiveDropdown(item.id);
                            }
                          }}
                        >
                          Actions ▼
                        </button>
                        {activeDropdown === item.id && (
                          <div
                            className={`action-dropdown-menu${
                              dropdownDirection === "up" ? " dropdown-up" : ""
                            }`}
                            role="menu"
                          >
                            <button
                              className="action-menu-item"
                              role="menuitem"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(item);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="action-menu-item danger"
                              role="menuitem"
                              disabled={deletingId === item.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdown(null);
                                handleDelete(item);
                              }}
                            >
                              {deletingId === item.id ? "Deleting..." : "Delete"}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {modalMode === "create" ?
                  "Create Process Category"
                : "Edit Process Category"}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Name"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Long Description"
                />
              </div>
              {formError && <div className="error-text">{formError}</div>}
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={saving}>
                  {saving ? "Saving..." : modalMode === "create" ? "Create" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProcessCategoryAttributesPage;

